import { z } from 'zod';

import { CURRENCIES } from '../constants/currency';
import { QUOTE_MODES } from '../constants/verticals';
import { cuidLike, nonEmptyString } from '../zod-common';

import type { DISCREPANCY_TYPES } from '../constants/verticals';

export type QuoteModeValue = (typeof QUOTE_MODES)[number];
export type DiscrepancyTypeValue = (typeof DISCREPANCY_TYPES)[number];

const moneyAmount = z
  .number()
  .nonnegative('Doit être ≥ 0')
  .max(1_000_000_000, 'Montant trop élevé');

const adjustmentAmount = z
  .number()
  .min(-1_000_000_000, 'Ajustement trop négatif')
  .max(1_000_000_000, 'Ajustement trop élevé');

const quantityAmount = z.number().nonnegative('Quantité ≥ 0').max(100_000, 'Quantité trop élevée');

export const QUOTE_LINE_COMMENT_MAX_LENGTH = 500;

export function clampQuoteLineComment(comment: string | null | undefined): string | null {
  if (comment === null || comment === undefined) {
    return null;
  }
  return comment.slice(0, QUOTE_LINE_COMMENT_MAX_LENGTH);
}

export const quoteLineInputSchema = z
  .object({
    id: z.string().optional(),
    expectationId: z.string().nullable().optional(),
    additionParentId: z.string().max(100, 'Identifiant trop long').nullable().optional(),
    bidderTitle: nonEmptyString.max(200, 'Max 200 caractères'),
    bidderDescription: z.string().trim().max(4_000, 'Max 4000 caractères').default(''),
    mode: z.enum(QUOTE_MODES),
    quantity: quantityAmount.nullable().optional(),
    unitPrice: moneyAmount.nullable().optional(),
    adjustment: adjustmentAmount.nullable().optional(),
    isProposedForDeletion: z.boolean().default(false),
    deletionReason: z.string().trim().max(200, 'Max 200 caractères').nullable().optional(),
    additionJustification: z.string().trim().max(200, 'Max 200 caractères').nullable().optional(),
    bidderComment: z
      .string()
      .trim()
      .max(QUOTE_LINE_COMMENT_MAX_LENGTH, 'Max 500 caractères')
      .nullable()
      .optional(),
    order: z.number().int().min(0),
  })
  .refine(
    (line) => {
      if (line.isProposedForDeletion) {
        return true;
      }
      if (line.mode === 'TIME_BASED_DAY' || line.mode === 'TIME_BASED_HOUR') {
        return line.quantity !== null && line.quantity !== undefined;
      }
      return true;
    },
    { message: 'Quantité requise en mode temps', path: ['quantity'] },
  );
export type QuoteLineInput = z.infer<typeof quoteLineInputSchema>;

export const updateBidQuoteSchema = z.object({
  bidId: cuidLike,
  mode: z.enum(QUOTE_MODES),
  globalAdjustment: adjustmentAmount.nullable().optional(),
  lines: z.array(quoteLineInputSchema).max(200, 'Max 200 lignes'),
});
export type UpdateBidQuoteInput = z.infer<typeof updateBidQuoteSchema>;

export const submitBidSchema = z.object({
  bidId: cuidLike,
  message: z
    .string()
    .trim()
    .min(1, 'Message de présentation requis')
    .max(2_000, 'Max 2000 caractères'),
});
export type SubmitBidInput = z.infer<typeof submitBidSchema>;

export const migrateBidSchema = z.object({
  bidId: cuidLike,
  mode: z.enum(QUOTE_MODES),
  globalAdjustment: adjustmentAmount.nullable().optional(),
  lines: z.array(quoteLineInputSchema).max(200, 'Max 200 lignes'),
});
export type MigrateBidInput = z.infer<typeof migrateBidSchema>;

export const quoteCurrencySchema = z.enum(CURRENCIES);
export type QuoteCurrencyValue = z.infer<typeof quoteCurrencySchema>;
